import React, { useEffect, useState } from 'react';
import { Badge, Modal, notification, Tabs, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  AnyAssetApplication,
  AssetRemoteAccessProfile,
  CompactTable,
  FieldError,
  Flex,
  IconStartConnection,
  Loading,
  openNotificationWithIcon,
  RemoteAccessProfile,
  RemoteRequest,
  Segment,
  useFetch,
  useUserinfo,
  VerticalRadio,
} from '@mss/mss-component-library';
import MssIcon from 'jsx/common/MssIcon';
import IconRequestConnection from 'jsx/common/img/IconRequestConnection';
import { Info, Lock } from 'react-feather';
import { Formik } from 'formik';
import DatePicker from 'formik-antd/lib/date-picker';
import Input from 'formik-antd/lib/input';
import Form from 'formik-antd/lib/form';
import { TFunction } from 'i18next';
import Button from 'jsx/common/Button';
import SubmitButton from 'jsx/common/SubmitButton';
import moment from 'moment';
import RequestStatus from 'jsx/common/RequestStatus';
import dateFormat from 'dateformat';
import RemoteRequestViewModal from 'jsx/remote-access/components/RemoteRequestViewModal';
import RemoteCreateConnection from './RemoteCreateConnection';
import Api from 'jsx/asset/services/Api';
import { array, object, string } from 'yup';
import { remoteRequestAny } from '../../authorization/util/permissionSets';
import classNames from 'classnames';

type RemoteConnectModalProps = {
  asset: AnyAssetApplication & { approved_remote_request_count: number };
  closeModal: () => void;
  visible?: boolean;
};

export const Heading = ({
  className,
  ...props
}: React.ComponentProps<'span'>) => {
  return (
    <span
      className={classNames('text-sm pl-2 font-normal', className)}
      {...props}
    />
  );
};

const RequestConnection = ({
  profile,
  closeModal,
  assetId,
}: {
  profile: any;
  closeModal: () => void;
  assetId: string;
}) => {
  const [t] = useTranslation(['remoteAccess', 'common']);
  const initialValues = {
    ticket_id: '',
    request_message: '',
    profile: profile.id,
    protocol: profile.protocol,
    dateRange: null,
    asset: assetId,
  };

  return (
    <Formik
      initialValues={initialValues}
      validateOnMount
      validationSchema={object().shape({
        ticket_id: string(),
        request_message: string().when('ticket_id', {
          is: (x: string) => !x || x.length === 0,
          then: string().required(
            t('Reason required without Ticket / Call ID')
          ),
        }),
        dateRange: array().min(2).required(),
      })}
      onSubmit={async values => {
        // if (values.dateRange === null) return;
        const resp = await Api.asset.remote.request(values as any);

        if (resp.status !== 201)
          openNotificationWithIcon(
            notification.error,
            t('Request access failed'),
            t(
              'Due to technical issues the request for remote access could not be send. Please try again later.'
            )
          );
        if (resp.status === 201) {
          openNotificationWithIcon(
            notification.success,
            t('Request created'),
            t('A request for remote access is created.')
          );
        }
        closeModal();
      }}
    >
      {({ values, errors }) => {
        const startDate = values.dateRange != null ? values.dateRange[0] : '';
        const isBeforeCurrentTime =
          new Date(startDate).getTime() - new Date().getTime() < 0;
        return (
          <Form>
            <Input type="hidden" name="profile" value={values.profile} />
            <Segment
              style={{ marginTop: 24 }}
              padding
              heading={
                <Heading>
                  {t('Provide the required request information')}
                </Heading>
              }
            >
              <table style={{ width: '100%' }}>
                <tbody>
                  <tr>
                    <td>{t('Ticket / Call ID')}:</td>
                    <td>
                      <Input value={values.ticket_id} name="ticket_id" />
                    </td>
                  </tr>
                  <tr>
                    <td style={{ minWidth: 180 }}>
                      {t('Reason for connecting')}:
                    </td>
                    <td>
                      <Input
                        value={values.request_message}
                        name="request_message"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>{t('Scheduled session')}:</td>
                    <td>
                      <DatePicker.RangePicker
                        name="dateRange"
                        style={{ width: '100%' }}
                        ranges={undefined}
                        showTime
                        disabledTime={(d: any) => !d || d.isBefore(moment())}
                        disabledDate={(d: any) =>
                          !d || d.isBefore(moment().add(-1, 'day'))
                        }
                        defaultValue={undefined}
                        fast
                      />
                      {isBeforeCurrentTime && (
                        <FieldError>
                          {t("The start date you've selected is in the past.")}
                        </FieldError>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </Segment>
            <Flex v="center" h="end" className={'pr-6 mt-4'}>
              <Button variant="primary" ghost onClick={closeModal}>
                {t('Cancel')}
              </Button>
              <SubmitButton
                disabled={Object.keys(errors).length > 0 || isBeforeCurrentTime}
                className="ml-2"
              >
                {t('Request')}
              </SubmitButton>
            </Flex>
          </Form>
        );
      }}
    </Formik>
  );
};

const NewRequest = ({ profiles, closeModal, assetId }: any) => {
  const [t] = useTranslation(['remoteAccess', 'common']);
  const [selectedProfile, selectProfile] = useState<AssetRemoteAccessProfile>();

  return (
    <Segment
      border
      padding
      heading={
        <Heading>{t('Select the preferred profile to connect.')}</Heading>
      }
    >
      {profiles.loading && <Loading />}
      {profiles.data && (
        <VerticalRadio.Group
          value={selectedProfile?.id}
          onChange={(e: any) =>
            selectProfile(
              profiles.data.find(
                (x: AssetRemoteAccessProfile) => x.id === e.target.value
              )
            )
          }
        >
          {profiles.data.map(
            (profile: AssetRemoteAccessProfile) =>
              (profile.create_remote_request ||
                profile.start_remote_session) && (
                <VerticalRadio
                  value={profile.id}
                  key={profile.id}
                  data-ta={profile.name}
                >
                  <Flex v="center" inline>
                    <span>
                      {profile.protocol.toUpperCase()} - {profile.name}
                    </span>
                    {!profile.start_remote_session && (
                      <Tooltip placement="right" title={t('Approval required')}>
                        <Lock size={12} className="ml-1" />
                      </Tooltip>
                    )}
                  </Flex>
                </VerticalRadio>
              )
          )}
        </VerticalRadio.Group>
      )}
      {!profiles.loading && !profiles.data && <>{t('No profiles found')}</>}
      {selectedProfile?.id && (
        <>
          {selectedProfile?.start_remote_session && (
            <RemoteCreateConnection
              closeModal={closeModal}
              profile={selectedProfile}
            />
          )}
          {!selectedProfile?.start_remote_session &&
            selectedProfile?.create_remote_request && (
              <RequestConnection
                assetId={assetId}
                closeModal={closeModal}
                profile={selectedProfile}
              />
            )}
        </>
      )}
      {!selectedProfile?.id && (
        <Flex v="center" h="end">
          <Button variant="primary" ghost onClick={closeModal}>
            {t('Cancel')}
          </Button>
          <Button variant="primary" disabled className="ml-2">
            {t('Connect')}
          </Button>
        </Flex>
      )}
    </Segment>
  );
};

const requestColumns = (t: TFunction, setModal: Function) => [
  {
    title: t('Profile'),
    dataIndex: 'profile_object',
    key: 'profile_name',
    render: (profile_object: AssetRemoteAccessProfile) => (
      <>
        {profile_object.protocol.toUpperCase()} - {profile_object.name}
      </>
    ),
  },
  {
    title: t('Status'),
    dataIndex: 'status',
    key: 'request_status',
    width: 160,
    render: (status: AssetRemoteAccessProfile) => (
      <RequestStatus status={status} />
    ),
  },
  {
    title: t('Start time'),
    dataIndex: 'approved_start_date',
    key: 'approved_start_date',
    width: 180,
    render: (record: any) =>
      record && dateFormat(record || '', 'mm-dd-yyyy, HH:MM:ss'),
  },
  {
    title: t('End time'),
    dataIndex: 'approved_end_date',
    key: 'approved_end_date',
    width: 180,
    render: (record: any) =>
      record && dateFormat(record || '', 'mm-dd-yyyy, HH:MM:ss'),
  },
  {
    title: '',
    width: 30,
    render: (record: any) => (
      <Flex
        h="center"
        style={{ cursor: 'pointer' }}
        onClick={e => {
          e.stopPropagation();
          setModal(record);
        }}
      >
        <Info size={14} />
      </Flex>
    ),
  },
];
type OpenRequestProps = {
  assetId: string;
  profiles: RemoteAccessProfile[];
  closeModal: () => void;
};

interface EnrichedRequest extends RemoteRequest {
  profile_object: AssetRemoteAccessProfile;
}

const OpenRequests = ({ assetId, closeModal }: OpenRequestProps) => {
  const [hasPermission] = useUserinfo();
  const [t] = useTranslation(['remoteAccess', 'common', 'profile']);
  const [selectedRequest, selectRequest] = useState<EnrichedRequest>();
  const [modal, setModal] = useState<EnrichedRequest | null>();
  const [selectedRowKeys, selectRow] = useState<string[]>();
  const [requests] = useFetch({
    url: `/api/remote-request/?profile__asset=${assetId}`,
    defaultValue: [],
    initialFetch: hasPermission(remoteRequestAny),
  });

  return (
    <>
      <CompactTable
        pagination={false}
        loading={requests.loading}
        dataSource={requests.data}
        columns={requestColumns(t, setModal)}
        rowKey={'id'}
        rowSelection={{
          type: 'radio',
          onSelect: (record: any) => {
            selectRow([record.id]);
            selectRequest(record);
          },
          selectedRowKeys,
          getCheckboxProps: (record: any) => {
            return { disabled: record.status.toLowerCase() !== 'allow' };
          },
        }}
        onRow={(record: any) => ({
          style: { cursor: 'default' },
          onClick: () => {
            if (record.status !== 'allow') return;
            selectRow([record.id]);
            selectRequest(record);
          },
        })}
      />
      {modal && (
        <RemoteRequestViewModal
          request={modal}
          closeModal={() => {
            setModal(null);
          }}
          visible={!!modal}
        />
      )}
      {selectedRequest && (
        <RemoteCreateConnection
          closeModal={closeModal}
          requestId={selectedRequest.id}
          profile={selectedRequest.profile_object}
        />
      )}
      {!selectedRequest && (
        <Flex v="center" h="end" className="mt-4">
          <Button variant="primary" ghost onClick={closeModal}>
            {t('Cancel')}
          </Button>
          <Button variant="primary" disabled className="ml-2">
            {t('Connect')}
          </Button>
        </Flex>
      )}
    </>
  );
};

function RemoteConnectModal({
  asset,
  closeModal,
  visible = false,
}: RemoteConnectModalProps) {
  const [t] = useTranslation(['remoteAccess', 'common']);
  const [hasPermission] = useUserinfo();
  const [approvedCount, setApprovedCount] = useState<number>(0);
  const [profiles, fetchProfiles, resetProfiles] = useFetch<
    RemoteAccessProfile[]
  >({
    url: `/api/assets/${asset?.id}/profiles/`,
    initialFetch: false,
    defaultValue: [],
    onSucces: result => {
      const approvedCount = result.data
        .map(x => x.approved_requests)
        .reduce((x, y) => (x || 0) + (y || 0));
      setApprovedCount(approvedCount || 0);
    },
  });
  useEffect(() => {
    if (visible) asset && fetchProfiles();
    else resetProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset, visible]);
  const close = () => {
    resetProfiles();
    closeModal();
  };

  return (
    <Modal
      onCancel={close}
      open={visible}
      destroyOnClose
      title={`${t('Remote Access')} / ${asset?.name}`}
      width={900}
      footer={null}
    >
      {visible && (
        <Tabs>
          <Tabs.TabPane
            tab={
              <>
                <MssIcon className="mr-2" icon={IconRequestConnection} />
                {t('New request')}
              </>
            }
            key="new"
          >
            <NewRequest
              assetId={asset.id}
              profiles={profiles}
              closeModal={close}
            />
          </Tabs.TabPane>
          <Tabs.TabPane
            tab={
              <>
                <MssIcon
                  className="mr-2"
                  icon={IconStartConnection}
                  data-ta="OpenRequestTab"
                />
                {t('Open requests')}

                {hasPermission(remoteRequestAny) && approvedCount > 0 && (
                  <Badge
                    count={approvedCount}
                    overflowCount={9}
                    className="ml-2"
                    color="#417e80"
                  />
                )}
              </>
            }
            key="open"
          >
            <OpenRequests
              assetId={asset.id!}
              profiles={profiles.data}
              closeModal={close}
            />
          </Tabs.TabPane>
        </Tabs>
      )}
    </Modal>
  );
}

export default RemoteConnectModal;
